import { useEffect, useRef, useState } from 'react';

import { Modal } from '~components/Modals/Base';
import { Captions, SubHeadline, Text } from '~components/Typography';
import { useTranslations } from '~i18n/hooks';
import type {
	BotWizardLoaderText,
	BotWizardQuote,
	Translated,
} from '~shared/api/sanity';
import { FadeInImage } from '~shared/ui/atoms/FadeInImage';
import { ProgressBar } from '~shared/ui/atoms/ProgressBar';
import { animateProgress } from '~shared/utils/loadingProgress';
import { replacePlaceholders } from '~utils/string';

import {
	imageCss,
	Quote,
	StyledLoadingModalWrapper,
	StyledQuotesCarousel,
	Wrapper,
} from './styles';

export const LoadingModal = ({
	open,
	name,
	isMale,
	quotes,
	loaderTexts,
}: {
	open: boolean;
	isMale: boolean;
	name: string;
	quotes: Translated<BotWizardQuote & { image: { asset: { url: string } } }>[];
	loaderTexts: Translated<BotWizardLoaderText>[];
}) => {
	const t = useTranslations('BotWizard');

	const [progress, setProgress] = useState(0);
	const animationRef = useRef<boolean>(true);

	const runAnimation = async () => {
		await animateProgress(0, 30, 3000, (value) => {
			if (animationRef.current) setProgress(value);
		});

		await animateProgress(30, 90, 10000, (value) => {
			if (animationRef.current) setProgress(value);
		});

		await animateProgress(90, 99, 5000, (value) => {
			if (animationRef.current) setProgress(value);
		});
	};

	useEffect(() => {
		animationRef.current = true;
		void runAnimation();

		return () => {
			animationRef.current = false;
		};
	}, [open]);

	const currentLoaderTextIndex = Math.floor(
		progress / (100 / loaderTexts.length)
	);

	return (
		<Modal
			locked={true}
			open={open}
			onClose={() => null}
			data-name="bw-loading-modal"
		>
			<StyledLoadingModalWrapper>
				<Captions color="grey-52-solid" size="L">
					{t('loadingPopupTitle')}
				</Captions>
				<Wrapper>
					<SubHeadline size="L">
						{replacePlaceholders(
							(isMale
								? loaderTexts[currentLoaderTextIndex].maleTitle
								: loaderTexts[currentLoaderTextIndex].femaleTitle) || '',
							{
								name,
							}
						)}
					</SubHeadline>
					<ProgressBar progress={progress} width={22} height={0.4} />
					<Captions color="grey-52-solid" size="L">
						{t('loadingPopupProgressText', {
							percent: progress,
						})}
						<br />
						{replacePlaceholders(
							(isMale
								? loaderTexts[currentLoaderTextIndex].maleText
								: loaderTexts[currentLoaderTextIndex].femaleText) || '',
							{
								name,
							}
						)}
					</Captions>
				</Wrapper>
				<StyledQuotesCarousel>
					{quotes.map((el) => (
						<Quote key={el._id}>
							<Text size="S">{el.text}</Text>
							{el.author && (
								<SubHeadline color="grey-68-solid" size="XS">
									{el.author}
								</SubHeadline>
							)}
							{el.image && (
								<FadeInImage
									className={imageCss}
									width={150}
									height={20}
									src={el.image.asset.url}
									alt={''}
								/>
							)}
						</Quote>
					))}
				</StyledQuotesCarousel>
			</StyledLoadingModalWrapper>
		</Modal>
	);
};
